import { observer } from 'mobx-react';
import { Fragment, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Button, Card, Divider, Flex, Text } from '@servicetitan/anvil2';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { Page, Spinner, StickyFooter } from '@servicetitan/web-feature-ui';

import { Content } from '../../common/components/content';
import { ErrorState } from '../../common/components/error-state';
import { RequestState } from '../../common/utils/request-states';
import { ServiceEntryDataStore } from '../stores/service-entry-data.store';
import { ServiceEntryViewStore } from '../stores/service-entry-view.store';

export const PreviewServiceEntryPage = provide({
    singletons: [ServiceEntryDataStore, ServiceEntryViewStore],
})(
    observer(() => {
        const [
            {
                serviceEntry: serviceEntryData,
                fetchServiceEntryData,
                cancelGetServiceEntryData,
                fetchServiceEntryDataRequestState,
            },
        ] = useDependencies(ServiceEntryDataStore);
        const location = useLocation();
        const history = useHistory();
        const [{ serviceEntry }] = useDependencies(ServiceEntryViewStore);

        const serviceEntryId = location.pathname.split('/').pop(); // useParams didn't work as expected in Azure deployment so had to revert to this

        const handleEditClick = () => {
            if (serviceEntryId) {
                history.push({
                    pathname: `/service-entries/${serviceEntryId}/edit`,
                    state: { serviceEntry: serviceEntryData },
                });
            }
        };

        const handleTryAgain = () => {
            if (serviceEntryId) {
                fetchServiceEntryData(Number(serviceEntryId));
            }
        };

        const handleGoBack = () => {
            history.goBack();
        };

        useEffect(() => {
            if (serviceEntryId) {
                fetchServiceEntryData(Number(serviceEntryId));
            }

            return cancelGetServiceEntryData;
        }, [fetchServiceEntryData, cancelGetServiceEntryData]);

        if (fetchServiceEntryDataRequestState === RequestState.Loading) {
            return (
                <Content justifyContent="center" alignItems="center">
                    <Flex direction="column" gap="5" alignItems="center">
                        <Spinner size="3.75rem" />
                        <Flex direction="column" gap="2" alignItems="center">
                            <Text variant="body" size="large">
                                <b>Loading Service Entry...</b>
                            </Text>
                            <Text variant="body" subdued style={{ textAlign: 'center' }}>
                                Please wait a moment while we fetch your service entry.
                            </Text>
                        </Flex>
                        <Button appearance="secondary" size="large" onClick={handleGoBack}>
                            Back to previous page
                        </Button>
                    </Flex>
                </Content>
            );
        }

        if (fetchServiceEntryDataRequestState === RequestState.Error) {
            return (
                <ErrorState
                    title="Unable to load Service Entry"
                    description="Something went wrong. Please try again."
                    onRetry={handleTryAgain}
                    onGoBack={handleGoBack}
                />
            );
        }

        if (fetchServiceEntryDataRequestState === RequestState.Success) {
            return (
                <Page>
                    <Page.Header title="Service Entry" />
                    <Page.Content className="a2-bg-strong">
                        <Flex direction="column" gap="6" className="a2-p-block-4 a2-p-inline-5">
                            <Flex direction="column" gap="2">
                                <Text variant="body">
                                    <b>General Information</b>
                                </Text>
                                <Card flexDirection="column" className="a2-p-inline-start-5">
                                    <Flex direction="column" gap="3">
                                        <Flex direction="column">
                                            <Text variant="eyebrow">Vehicle Name</Text>
                                            <Text variant="body">{serviceEntry.vehicleName}</Text>
                                        </Flex>
                                        <Flex direction="column">
                                            <Text variant="eyebrow">VIN</Text>
                                            <Text variant="body">{serviceEntry.vin}</Text>
                                        </Flex>
                                        <Flex direction="column">
                                            <Text variant="eyebrow">Category</Text>
                                            <Text variant="body">{serviceEntry.category}</Text>
                                        </Flex>
                                        <Flex direction="column">
                                            <Text variant="eyebrow">Service Date</Text>
                                            <Text variant="body">{serviceEntry.serviceDate}</Text>
                                        </Flex>
                                        <Divider />
                                        <Flex direction="column">
                                            <Text variant="eyebrow">Odometer</Text>
                                            <Text variant="body">Mi {serviceEntry.odometer}</Text>
                                        </Flex>
                                        <Flex direction="column">
                                            <Text variant="eyebrow">Engine Hours</Text>
                                            <Text variant="body">
                                                Hrs {serviceEntry.engineHours}
                                            </Text>
                                        </Flex>
                                    </Flex>
                                </Card>

                                <Text variant="body">
                                    <b>Service Checks</b>
                                </Text>
                                <Card
                                    flexDirection="column"
                                    gap="3"
                                    className="a2-p-inline-start-5"
                                >
                                    <Flex direction="column" gap="3">
                                        {serviceEntry.serviceRecords?.map(serviceRecord => (
                                            <Fragment key={serviceRecord.serviceType}>
                                                <Flex direction="column">
                                                    <Text variant="eyebrow">Service Type</Text>
                                                    <Text variant="body">
                                                        {serviceRecord.serviceType}
                                                    </Text>
                                                </Flex>
                                                <Flex direction="column">
                                                    <Text variant="eyebrow">Service Cost</Text>
                                                    <Text variant="body" size="small">
                                                        ${serviceRecord.cost}
                                                    </Text>
                                                </Flex>
                                                <Divider />
                                            </Fragment>
                                        ))}
                                    </Flex>
                                    <Flex justifyContent="space-between">
                                        <Text variant="eyebrow">
                                            <b>Total Cost</b>
                                        </Text>
                                        <Text variant="body" size="small">
                                            <b>${serviceEntry.totalCost.toFixed(2)}</b>
                                        </Text>
                                    </Flex>
                                </Card>

                                <Text variant="body">
                                    <b>Other Details</b>
                                </Text>
                                <Card flexDirection="column" className="a2-p-inline-start-5">
                                    <Flex direction="column" gap="3">
                                        <Flex direction="column">
                                            <Text variant="eyebrow">Service center</Text>
                                            <Text variant="body">{serviceEntry.serviceCenter}</Text>
                                        </Flex>
                                        <Flex direction="column">
                                            <Text variant="eyebrow">Invoice Number</Text>
                                            <Text variant="body">{serviceEntry.invoiceNumber}</Text>
                                        </Flex>
                                        <Flex direction="column">
                                            <Text variant="eyebrow">Notes</Text>
                                            <Text variant="body">{serviceEntry.notes}</Text>
                                        </Flex>
                                        {serviceEntry.fileAttachments &&
                                            serviceEntry.fileAttachments.length > 0 && (
                                                <>
                                                    <Divider />
                                                    <Flex direction="column" gap="2">
                                                        <Text variant="eyebrow">Attachments</Text>
                                                        <Flex direction="column" gap="2">
                                                            {serviceEntry.fileAttachments.map(
                                                                file => (
                                                                    <Flex
                                                                        key={file.id}
                                                                        justifyContent="space-between"
                                                                        alignItems="center"
                                                                    >
                                                                        <Text
                                                                            variant="body"
                                                                            size="small"
                                                                            style={{
                                                                                wordBreak:
                                                                                    'break-all',
                                                                                flex: 1,
                                                                            }}
                                                                        >
                                                                            {file.name}
                                                                        </Text>
                                                                        {/* <Button
                                                                            size="small"
                                                                            appearance="secondary"
                                                                            icon={IconDownload}
                                                                            aria-label={`Download ${file.name}`}
                                                                            onClick={() => {
                                                                                const url =
                                                                                    downloadFileAttachment(
                                                                                        file.id,
                                                                                        baseUrl
                                                                                    );

                                                                                File.download({
                                                                                    filename:
                                                                                        file.name,
                                                                                    url,
                                                                                });
                                                                            }}
                                                                        /> */}
                                                                    </Flex>
                                                                )
                                                            )}
                                                        </Flex>
                                                    </Flex>
                                                </>
                                            )}
                                    </Flex>
                                </Card>
                            </Flex>
                        </Flex>
                        <StickyFooter>
                            <Button
                                appearance="primary"
                                style={{ width: '100%' }}
                                onClick={handleEditClick}
                            >
                                Edit Service Entry
                            </Button>
                        </StickyFooter>
                    </Page.Content>
                </Page>
            );
        }
    })
);
